// HandoverBody — the chains a person holds in the project, one line per place, each
// with a successor field (ADR-081 §5): ONE body for the leave dialog and the disable
// warning's "Hand over…". The successor is picked through the person lookup's
// `accountable` rule of THAT place (the same source every chain editor uses — "Will
// be granted scope" on a pick whose scope does not reach it, ADR-052 §5); the departing
// person's own row comes back greyed with its reason (a person cannot succeed
// themselves — a refused row is shown, never picked); none = the person
// simply leaves the chain — the next moves up (a lead), the lead stays (a backup), or
// a gap remains — and the line says which. The host owns the picks; a line hands its
// pick up WITH what the field's row said (the name, whether the person's scope already
// reaches the place), so the backing consent names people and places, never codes
// (`useBackingConsent`). The places and the picks are props; the words are translated
// here and the rows read here.

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { DepartureChain } from '@ems-app/schemas/users/responses';
import type { UserLookupRow } from '@ems-app/schemas/users/lookup';
import { personSource, useNamedPeople } from '../../hooks/useUsers.js';
import { formatList } from '../../lib/format.js';
import { useAccountableFace, usePersonFace } from '../../components/patterns/lookupFaces.js';
import { Picker } from '../../components/patterns/Picker.js';

/** The place's key — a chain is a responsibility at a place. */
export function chainKey(c: Pick<DepartureChain, 'responsibility_id' | 'scope_id'>): string {
  return `${c.responsibility_id}:${c.scope_id}`;
}

/** A successor as picked: the id the wire carries, and what the field's row said — the person's name, and whether
 *  their scope already reaches the place (`covered`, the accountable rule's fact) — both absent when no row was met. */
export interface SuccessorPick {
  id: string;
  name?: string;
  covered?: boolean;
}

const personName = (row: UserLookupRow): string => `${row.first_name} ${row.last_name}`.trim();

export interface HandoverBodyProps {
  projectId: string;
  /** The departing person — never offered as their own successor. */
  userId: string;
  chains: readonly DepartureChain[];
  /** The successor picked per chain key (absent or null = none). */
  successors: Readonly<Record<string, SuccessorPick | null>>;
  onChange: (key: string, successor: SuccessorPick | null) => void;
  disabled?: boolean;
}

export function HandoverBody({ projectId, userId, chains, successors, onChange, disabled }: HandoverBodyProps) {
  const { t } = useTranslation('users');
  return (
    <ul className="flex flex-col gap-3" aria-label={t('departure.listLabel')}>
      {chains.map((c) => (
        <HandoverLine key={chainKey(c)} projectId={projectId} userId={userId} chain={c} successor={successors[chainKey(c)] ?? null} onChange={(pick) => onChange(chainKey(c), pick)} disabled={disabled} />
      ))}
    </ul>
  );
}

function HandoverLine({ projectId, userId, chain, successor, onChange, disabled }: { projectId: string; userId: string; chain: DepartureChain; successor: SuccessorPick | null; onChange: (pick: SuccessorPick | null) => void; disabled?: boolean }) {
  const { t } = useTranslation('users');
  const accountable = useAccountableFace();
  const personFace = usePersonFace();
  // The departing person's own row is REFUSED (greyed, unselectable — the family's refused row, ADR-080 §5) with its
  // reason on the second line and no scope chip: nothing would make it pickable.
  const face = useMemo(
    () => (row: UserLookupRow) => {
      if (row.id !== userId) return accountable(row);
      const base = personFace(row);
      return { ...base, description: [base.description, t('departure.notSelf')].filter(Boolean).join(' · '), disabled: true };
    },
    [accountable, personFace, userId, t],
  );
  // The successor's source: the people who may be named at THIS place (the `accountable` rule — ADR-078 §6).
  const source = useMemo(() => personSource(projectId, 'accountable', face, { level: chain.level, scope: chain.scope_id }), [projectId, face, chain.level, chain.scope_id]);
  // The rows met at this place (the field's own cache entry on the source) name the pick as it is made.
  const { byId } = useNamedPeople(projectId, successor ? [successor.id] : [], { rule: 'accountable', level: chain.level, scope: chain.scope_id });
  const pickOf = (id: string | null): SuccessorPick | null => {
    if (id === null) return null;
    const row = byId.get(id);
    return row ? { id, name: personName(row), covered: row.covered } : { id };
  };
  // A place outside the viewer's reach is named by its kind alone (ADR-066 §9).
  const place = chain.scope_label ?? t(`access.outside.${chain.level}`);
  const position = t(`departure.position.${chain.position}`);
  const remains = chain.alone ? t('departure.nobodyRemains') : t('departure.remain', { count: chain.remaining_active });
  // None: a lead's next moves up; a backup just leaves and the lead stays; alone, the place is left with no one.
  const none = chain.alone ? t('departure.leaveGap') : chain.position === 'backup' ? t('departure.backupLeaves') : t('departure.nextMovesUp');
  return (
    <li className="flex flex-col gap-2 rounded-md border border-[var(--color-border)] p-3">
      <div className="flex flex-col gap-0.5">
        <span className="font-medium">
          <bdi>{chain.responsibility_name}</bdi> · <bdi>{place}</bdi>
        </span>
        <span className="text-sm text-[var(--color-text-secondary)]">
          {position} · {remains}
        </span>
      </div>
      {/* The field's name carries the place: the accessible name and the phone sheet's title say which chain is asked. */}
      <Picker
        source={source}
        value={successor?.id ?? null}
        onChange={(id) => onChange(pickOf(id))}
        label={t('departure.successorFor', { responsibility: chain.responsibility_name, place })}
        placeholder={none}
        clearable
        disabled={disabled}
        name={`successor-${chainKey(chain)}`}
      />
    </li>
  );
}

/** The backing consent's words (ADR-052 §5) from the picks: the NAMES of the people the server answered unbacked and
 *  the PLACES they are named to without their scope reaching them — every pick of such a person whose row did not say
 *  `covered` (a row not met counts). A name the picks do not carry is asked of the members lookup; "…" until it lands. */
export function useBackingConsent(
  projectId: string,
  chains: readonly DepartureChain[],
  picks: Readonly<Record<string, SuccessorPick | null>>,
  unbacked: readonly string[] | null,
): { place: string; names: string[] } {
  const { t } = useTranslation('users');
  const ids = unbacked ?? [];
  const named = new Map<string, string>();
  for (const p of Object.values(picks)) if (p?.name) named.set(p.id, p.name);
  const { byId } = useNamedPeople(projectId, ids.filter((id) => !named.has(id)), { rule: 'members', enabled: unbacked !== null });
  const names = ids.map((id) => {
    const row = byId.get(id);
    return named.get(id) ?? (row ? personName(row) : '…');
  });
  const set = new Set(ids);
  const places = chains
    .filter((c) => {
      const p = picks[chainKey(c)];
      return p != null && set.has(p.id) && p.covered !== true;
    })
    .map((c) => c.scope_label ?? t(`access.outside.${c.level}`));
  return { place: formatList([...new Set(places)]), names };
}
